/**
 * Accès aux données de prospection.
 *
 * Seule couche qui parle à Supabase pour les dossiers, les notes et les
 * réglages. Elle traduit les lignes SQL (snake_case) vers la forme attendue
 * par le domaine (camelCase), pour que le reste de l'app ignore d'où viennent
 * les données.
 */

import { DEFAULT_SETTINGS } from '../domain/constants.js'
import { recordKey } from '../domain/record.js'
import { supabase } from './client.js'

const rowToRecord = (row) => ({
  kind: row.kind,
  id: row.establishment_id,
  name: row.establishment_name,
  parentId: row.parent_id,
  status: row.status,
  pros: row.pros,
  since: row.since,
  paymentDay: row.payment_day,
  updatedAt: row.updated_at,
})

const rowToNote = (row) => ({
  id: row.id,
  kind: row.kind,
  establishmentId: row.establishment_id,
  establishmentName: row.establishment_name,
  body: row.body,
  createdAt: row.created_at,
  authorId: row.author_id,
  authorEmail: row.author_email,
})

const rowToSettings = (row) => ({
  tarif: Number(row?.tarif ?? DEFAULT_SETTINGS.tarif),
  ratioPro: Number(row?.ratio_pro ?? DEFAULT_SETTINGS.ratioPro),
})

/** Regroupe les notes par établissement, les plus récentes d'abord. */
function groupNotes(rows) {
  const map = new Map()
  for (const row of rows) {
    const note = rowToNote(row)
    const key = recordKey(note.kind, note.establishmentId)
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(note)
  }
  for (const list of map.values()) {
    list.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }
  return map
}

/** Charge l'intégralité des données en trois requêtes parallèles. */
export async function fetchAll() {
  const [recordsRes, notesRes, settingsRes] = await Promise.all([
    supabase.from('records').select('*'),
    supabase.from('notes').select('*'),
    supabase.from('settings').select('*').eq('id', 1).maybeSingle(),
  ])

  for (const res of [recordsRes, notesRes, settingsRes]) {
    if (res.error) throw res.error
  }

  const records = new Map(
    recordsRes.data.map((row) => [recordKey(row.kind, row.establishment_id), rowToRecord(row)]),
  )

  return {
    records,
    notes: groupNotes(notesRes.data),
    settings: rowToSettings(settingsRes.data),
  }
}

/**
 * Crée ou met à jour un dossier.
 *
 * Dernière écriture gagnante, assumé : à trois personnes sur 816 fiches, deux
 * saisies simultanées sur la même clinique sont improbables, et le coût d'un
 * verrouillage optimiste (conflits à arbitrer, UI de résolution) dépasserait
 * largement le risque. Les notes, elles, échappent à cette règle : chacune est
 * une ligne à part, donc rien ne s'écrase.
 */
/**
 * @param entity  l'entité commerciale — GMF ou département. Son identifiant
 *   sert de clé, son nom est enregistré comme instantané de diagnostic
 *   (voir schema.sql), et parentId rattache un département à son hôpital.
 */
export async function saveRecord(entity, patch) {
  const { data, error } = await supabase
    .from('records')
    .upsert(
      {
        kind: entity.kind,
        establishment_id: entity.id,
        establishment_name: entity.nom,
        parent_id: entity.parentId ?? null,
        status: patch.status,
        pros: patch.pros,
        since: patch.since,
        payment_day: patch.paymentDay ?? null,
      },
      { onConflict: 'kind,establishment_id' },
    )
    .select()
    .single()

  if (error) throw error
  return rowToRecord(data)
}

/**
 * Supprime un département.
 *
 * Ses notes suivent, y compris celles des collègues : le déclencheur
 * records_cascade_notes s'en charge côté base (voir schema.sql). Les supprimer
 * d'ici échouerait en silence sur celles dont on n'est pas l'auteur.
 */
export async function deleteDepartment(deptId) {
  const { error } = await supabase
    .from('records')
    .delete()
    .eq('kind', 'dept')
    .eq('establishment_id', deptId)
  if (error) throw error
}

export async function addNote(establishment, body, user) {
  const { data, error } = await supabase
    .from('notes')
    .insert({
      kind: establishment.kind,
      establishment_id: establishment.id,
      establishment_name: establishment.nom,
      body: body.trim(),
      author_id: user.id,
      author_email: user.email,
    })
    .select()
    .single()

  if (error) throw error
  return rowToNote(data)
}

/** RLS limite la suppression à ses propres notes ; une note d'autrui échoue côté serveur. */
export async function deleteNote(noteId) {
  const { error } = await supabase.from('notes').delete().eq('id', noteId)
  if (error) throw error
}

export async function saveSettings(settings) {
  const { data, error } = await supabase
    .from('settings')
    .update({ tarif: settings.tarif, ratio_pro: settings.ratioPro })
    .eq('id', 1)
    .select()
    .single()

  if (error) throw error
  return rowToSettings(data)
}

/**
 * S'abonne aux changements des autres postes.
 *
 * Les événements de sa propre écriture reviennent aussi : les gestionnaires
 * doivent donc être idempotents. C'est le cas — ils remplacent l'entrée par sa
 * version serveur plutôt que d'accumuler.
 *
 * @returns {() => void} pour se désabonner
 */
export function subscribeToChanges({
  onRecord,
  onRecordDelete,
  onNoteInsert,
  onNoteDelete,
  onSettings,
}) {
  const channel = supabase
    .channel('crm-changes')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'records' }, (payload) => {
      if (payload.eventType === 'DELETE') {
        // Postgres ne renvoie que la clé primaire sur DELETE (REPLICA IDENTITY
        // par défaut) : de quoi identifier la ligne, pas de la reconstruire.
        onRecordDelete?.(payload.old.kind, payload.old.establishment_id)
        return
      }
      onRecord?.(rowToRecord(payload.new))
    })
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notes' }, (payload) => {
      onNoteInsert?.(rowToNote(payload.new))
    })
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'notes' }, (payload) => {
      // Postgres ne renvoie que la clé primaire sur DELETE (REPLICA IDENTITY
      // par défaut) : d'où l'identifiant seul, sans l'établissement.
      onNoteDelete?.(payload.old.id)
    })
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'settings' }, (payload) => {
      onSettings?.(rowToSettings(payload.new))
    })
    .subscribe()

  return () => supabase.removeChannel(channel)
}
